import React, { useEffect, useState, useRef } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import { WaitlistCTA } from './WaitlistCTA';
import { FloatingCtaProvider, useFloatingCtaState } from './floatingCtaContext';
import { FEATURE_MENU_GROUPS, getFeatures } from '../lib/features';
import { TALK_TO_SALES_URL, SUPPORT_EMAIL, SUPPORT_MAILTO } from '../lib/links';
import { FeatureIcon } from './FeatureIcon';
// Map top-level nav labels to their live routes.
// NOTE: Enterprise, Security and Resources/Blog were moved to the noindex
// /unlisted namespace, so they're intentionally NOT linked from the nav.
const NAV_ROUTES: Record<string, string> = {
  Pricing: '/pricing',
  ChatGPT: '/chatgpt',
  Integrations: '/integrations',
  'Become an Affiliate': 'https://partners.dub.co/getgiga/affiliate-program'
};
// Nav labels whose target is an EXTERNAL site — rendered as a real anchor that
// opens in a new tab rather than an internal router Link.
const EXTERNAL_NAV = new Set(['Become an Affiliate']);
// Labels shown in the home-page top nav.
const NAV_LABELS = ['Features', 'Integrations', 'Pricing', 'ChatGPT'];
export function Layout() {
  return <FloatingCtaProvider data-id="element-4171" data-mp="1rmtiivmv0ykr">
      <LayoutInner data-id="element-4172" data-mp="1wk3cmol71yex" />
    </FloatingCtaProvider>;
}
function LayoutInner() {
  // While a section (e.g. the integration "plugs into your stack" beat) owns
  // its own primary CTA, it suppresses this global floating "Try Giga for free"
  // button so the two don't compete. It fades back in once that beat passes.
  const floatingCtaSuppressed = useFloatingCtaState();
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('up');
  // True when the floating mobile "Try Giga for free" CTA is sitting over an
  // ORANGE surface (the founder note, the footer, etc.) — the CTA is orange
  // too, so it would vanish against those. When that happens we flip the
  // button to a white treatment so it stays visible. Any orange section can
  // opt in by tagging its element with data-cta-invert; the footer is tagged
  // too.
  const [overOrange, setOverOrange] = useState(false);
  const footerRef = useRef<HTMLElement | null>(null);
  const lastScrollY = useRef(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openSection, setOpenSection] = useState<string | null>(null);
  const [featureMenuDismissed, setFeatureMenuDismissed] = useState(false);
  const features = getFeatures();
  const featureGroups = FEATURE_MENU_GROUPS.map(group => ({
    ...group,
    items: group.slugs.map(slug => features.find(feature => feature.slug === slug)).filter(Boolean)
  }));
  const location = useLocation();
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenSection(null);
  }, [location.pathname]);
  const toggleSection = (section: string) => setOpenSection(prev => prev === section ? null : section);
  // These routes open with a dark (#1C1510 / #1A1410) hero, so while the header
  // is still transparent (before the user scrolls) the nav must switch to a
  // light treatment — white links and a white logo — to stay legible. Once
  // scrolled, the header turns solid white and reverts to the dark-on-light
  // treatment.
  const darkHeroRoutes = ['/unlisted/compare/chatgpt', '/unlisted/security', '/demo', '/unlisted/team-workspace'];
  const isTeamWorkspace = location.pathname === '/unlisted/team-workspace';
  // NOTE: `/features/*` used to be listed here, back when those pages opened on
  // an ink hero. They now open on the cream paper ground like the rest of the
  // site, so the light treatment left white links and a white logo sitting on
  // near-white — the nav was effectively invisible until you scrolled.
  const onDark = darkHeroRoutes.includes(location.pathname) && !isScrolled;
  // Both full-screen home heroes own their primary action at the top of the
  // page, so the header uses a clear white Log in action until scrolling begins.
  const atHeroTop = (location.pathname === '/ai-coworker' || isTeamWorkspace) && !isScrolled;
  // /alt owns a sticky pillar nav of its own, and two stacked bars competing
  // for the top of the viewport is one too many — so there the site header
  // scrolls away with the page and the pillar nav takes the top edge.
  const headerScrollsAway = location.pathname === '/';
  // The home page opens on a full-bleed photographic plate that the header
  // floats over, so the header's actions need their dark-plate treatment
  // there — as they do on the other dark-hero routes.
  const onPlate = onDark || headerScrollsAway && !isScrolled;
  // The LINKS follow the same plate test as the actions beside them. Keyed to
  // `onDark` alone they stayed grey over the home page's photograph while the
  // three actions had already gone white — half the bar legible, half of it
  // sinking into the image.
  const navLinkClass = `text-sm font-semibold transition-colors ${onPlate ? 'text-white hover:text-[#F7C9AC]' : 'text-neutral-600 hover:text-[#2B211A]'}`;
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrolled = currentScrollY > 20;
      setIsScrolled(scrolled);
      if (scrolled) {
        if (currentScrollY > lastScrollY.current + 5) {
          setScrollDirection('down');
          setMobileMenuOpen(false);
        } else if (currentScrollY < lastScrollY.current - 5) {
          setScrollDirection('up');
        }
      } else {
        setScrollDirection('up');
      }
      lastScrollY.current = currentScrollY;
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Watch every orange surface (the footer + any section tagged with
  // data-cta-invert, e.g. the founder note): when one of them is under the
  // bottom band of the viewport — exactly where the floating mobile CTA sits —
  // flip the CTA to its white treatment so the orange button doesn't blend
  // into the orange background. The negative top rootMargin shrinks the
  // observed region to just the bottom ~12% strip, so the flip happens only
  // while the CTA truly overlaps the orange surface (not the moment any part
  // of a tall orange section first peeks into view). Re-runs on route change
  // so it picks up the orange sections present on the current page.
  useEffect(() => {
    const surfaces = new Set<Element>();
    document.querySelectorAll('[data-cta-invert]').forEach(el => surfaces.add(el));
    if (footerRef.current) surfaces.add(footerRef.current);
    if (surfaces.size === 0) {
      setOverOrange(false);
      return;
    }
    const active = new Set<Element>();
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) active.add(entry.target);else active.delete(entry.target);
      });
      setOverOrange(active.size > 0);
    }, {
      // Only the bottom strip of the viewport counts — that's where the
      // floating CTA lives.
      rootMargin: '-88% 0px 0px 0px',
      threshold: 0
    });
    surfaces.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [location.pathname]);
  return <div className="min-h-screen bg-[#FAF8F4] text-[#2B211A] font-body selection:bg-[#EA580C]/20 selection:text-[#2B211A]" data-id="element-4173" data-mp="1deu96etnckkv">
      <header className={`${headerScrollsAway ? 'absolute' : 'fixed'} top-0 w-full z-50 translate-y-0 transition-[background-color,padding,box-shadow] duration-300 ${isScrolled && !headerScrollsAway ? 'bg-white/90 backdrop-blur-md border-b border-neutral-200 shadow-sm py-3' : 'bg-transparent py-5'}`} data-id="element-4174" data-mp="14bt7cqbtdfn0">
        <div className="container mx-auto px-6 flex items-center justify-between" data-id="element-4175" data-mp="gmqndbtpjapc">
          <Link to="/" aria-label="Giga home" className="flex items-center gap-2 group" data-id="element-4176" data-mp="ko2ifwvcoo4x">
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className={`h-7 w-7 object-contain group-hover:scale-105 transition-transform ${onPlate ? '[filter:brightness(0)_invert(1)]' : ''}`} data-id="element-4177" data-mp="kpwsuh8zwxur" />

            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className={`h-6 object-contain transition-[filter] ${onPlate ? '[filter:brightness(0)_invert(1)]' : ''}`} data-id="element-4178" data-mp="m6u525322lma" />
          </Link>

          <nav className="hidden md:flex items-center gap-7" data-id="element-4179" data-mp="8ee8nn4ntluc">
            {NAV_LABELS.map(label => label === 'Features' ? <div key={label} className="relative group py-2" onMouseLeave={() => setFeatureMenuDismissed(false)} data-id="element-4180" data-mp="3gm7s3m1epe0">
                  <button type="button" className={`${navLinkClass} inline-flex items-center gap-1`} aria-haspopup="menu" onFocus={() => setFeatureMenuDismissed(false)} data-id="element-4181" data-mp="3ukufb5qorevv">Products <ChevronDown size={14} data-id="element-4182" data-mp="262rqb876lwsi" /></button>
                  {/* THE MENU IS A SURFACE FROM THIS SITE, not a default
                   dropdown. What made it read as off-brand: cool neutral-200
                   hairlines and a generic shadow-2xl on pure white, and an
                   orange-tinted tile behind every one of ~17 icons — seventeen
                   filled swatches turning a list of links into a wall of
                   badges, and spending the accent colour on navigation rather
                   than on anything the reader should act on.
                   Now: the site's warm hairline, 22px radius, the two-part
                   shadow used by every card, hairline rules between the three
                   columns instead of gaps, and bare icons that stay quiet
                   until a row is hovered. */}
                  {/* INK, NOT WHITE.
                   White was the wrong call for one concrete reason: this menu
                   opens over BOTH grounds. On cream it was a barely-there
                   value step — a white card on near-white paper, held apart
                   only by its shadow — and over the home page's dark
                   photographic hero it was a large white rectangle punched
                   into the plate. Ink solves both: it separates cleanly from
                   cream, and it belongs on the hero.
                   It also matches what the site already does with dark. Every
                   ink surface here is somewhere you look INTO — the Company
                   Brain console, the closing panels — and a menu is exactly
                   that: a temporary panel over the page, not another card
                   sitting in the page's own stack. Same 22px radius, same
                   halftone, so it reads as one of ours. */}
                  <div className={`invisible absolute left-1/2 top-full w-[780px] -translate-x-1/2 translate-y-1.5 overflow-hidden rounded-[22px] border border-white/[0.08] bg-[#1A120C] p-2 opacity-0 shadow-[0_2px_6px_rgba(20,13,8,0.28),0_18px_40px_-16px_rgba(20,13,8,0.5),0_52px_100px_-44px_rgba(20,13,8,0.8)] transition-[opacity,transform,visibility] duration-200 ease-out ${featureMenuDismissed ? '' : 'group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100'}`} data-id="element-4183" data-mp="3dhj1u1br9njr">
                    <div className="dot-matrix pointer-events-none opacity-[0.07]" aria-hidden="true" data-id="element-4184" data-mp="1gnet4t60eysj" />
                    <div className="relative grid grid-cols-3 divide-x divide-white/[0.07]" role="menu" data-id="element-4185" data-mp="1bvdmbmrm209w">
                      {featureGroups.map(group => <div key={group.key} className="px-2.5 py-2 first:pl-2 last:pr-2" data-id="element-4186" data-mp="17v35ff2a5pg9">
                        <p className="px-2.5 pb-2.5 text-[11px] font-semibold tracking-[0.02em] text-[#9A8B7C]" data-id="element-4187" data-mp="12xbbyglzlv9y">{group.label}</p>
                        <div data-id="element-4188" data-mp="xyi351hnasg4">
                          {group.items.map(feature => feature && <Link key={feature.slug} to={`/features/${feature.slug}`} role="menuitem" onClick={() => setFeatureMenuDismissed(true)} className="group/item flex items-center gap-2.5 rounded-[10px] px-2.5 py-[7px] text-left transition-colors duration-150 ease-out hover:bg-white/[0.06]" data-id="element-4189" data-mp="sy3zrwqzo6of">
                            <FeatureIcon name={feature.icon} size={15} className="shrink-0 text-white/35 transition-colors duration-150 ease-out group-hover/item:text-[#FB923C]" data-id="element-4190" data-mp="ab39s1hp0yyn" />
                            <span className="min-w-0 text-[13.5px] font-semibold leading-snug text-[#C9BFB4] transition-colors duration-150 ease-out group-hover/item:text-[#FAF8F4]" data-id="element-4191" data-mp="121r8z7zt3u9o">{feature.menuLabel || feature.name}</span>
                          </Link>)}
                        </div>
                      </div>)}
                    </div>
                  </div>
                </div> : EXTERNAL_NAV.has(label) ? <a key={label} href={NAV_ROUTES[label]} target="_blank" rel="noopener noreferrer" className={navLinkClass} data-id="element-4192" data-mp="1ky96y7590qed">
                  {label}
                </a> : <Link key={label} to={NAV_ROUTES[label]} className={`${navLinkClass} inline-flex items-center gap-1.5`} data-id="element-4193" data-mp="3agfw24j04hy9">
                  {label}{label === 'ChatGPT' && <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${onPlate ? 'bg-[#EA580C]/25 text-[#FDBA74]' : 'bg-[#EA580C]/10 text-[#EA580C]'}`} data-id="element-4194" data-mp="35gtjlxazz5qi">New</span>}
                </Link>)}
          </nav>

          <div className="flex items-center gap-3 md:gap-4" data-id="element-4195" data-mp="2rhlj9133lrkq">
            {/* Three actions in ascending commitment: a plain text Log in, an
             outlined Talk to sales, and one filled Get started. No
             scroll-dependent swap — the same three show at every scroll
             position, so the reader never loses the action they were
             reaching for. Only the TREATMENT changes: over the home
             photograph the two visible controls take their dark-plate
             versions so they stay legible. */}
            <WaitlistCTA analyticsLabel="nav-log-in" spinnerClassName="text-[#2B211A]" className={`hidden md:block text-sm font-semibold transition-colors ${onPlate ? 'text-white hover:text-[#F7C9AC]' : 'text-neutral-600 hover:text-[#2B211A]'}`} data-id="element-4196" data-mp="bpe5xte31iq0">
              Log in
            </WaitlistCTA>
            {/* The outlined action stays UNFILLED, at rest and on hover. A
             solid near-white hover state painted an opaque capsule over the
             page's dotted cream ground — the texture stopped dead at the
             pill's edge, which is what made it read as a sticker sitting on
             the header rather than a control cut into it. The hover is now a
             translucent warm wash, so the dots continue through it and only
             the value shifts. */}
            <a href={TALK_TO_SALES_URL} target="_blank" rel="noopener noreferrer" className={`hidden rounded-full border bg-transparent px-5 py-2.5 text-sm font-bold transition-[background-color,border-color,color] duration-200 ease-out md:block ${onPlate ? 'border-white/50 text-white hover:bg-white/10' : 'border-[#E7E0D4] text-[#2B211A] hover:border-[#D9CFBE] hover:bg-[#EA580C]/[0.06]'}`} data-id="element-4197" data-mp="3e58meauw0mf3">
              Talk to sales
            </a>
            <WaitlistCTA analyticsLabel="nav-try-giga" spinnerClassName={onPlate ? 'text-[#2B211A]' : 'text-white'} className={`hidden md:block px-5 py-2.5 rounded-full text-sm font-bold transition-colors shadow-sm ${onPlate ? 'bg-white text-[#2B211A] hover:bg-[#F5EFE7]' : 'bg-[#EA580C] hover:bg-[#C2410C] text-white'}`} data-id="element-4198" data-mp="3t6p75bthd08f">
              Get started
            </WaitlistCTA>

            {/* Mobile control: hamburger, plus a compact "Try for free" CTA.
             The header is only revealed while scrolling UP (it slides away on
             scroll-down), so showing the CTA here gives the user a quick
             conversion tap right next to the menu when they scroll back up.
             Hidden at the very top of the hero, where the hero already shows
             its own primary CTA. */}
            <div className="md:hidden flex items-center gap-2" data-id="element-4199" data-mp="yqop5egx399w">
              {isScrolled && <WaitlistCTA analyticsLabel="mobile-try-free" className="bg-[#EA580C] hover:bg-[#C2410C] text-white px-4 py-2 rounded-full text-sm font-bold transition-colors shadow-sm whitespace-nowrap" data-id="element-4200" data-mp="2t150ohwktirb">
                  Try for free
                </WaitlistCTA>}
              <button className={`p-2 -mr-2 transition-colors ${onPlate ? 'text-white' : 'text-[#2B211A]'}`} onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle mobile menu" aria-expanded={mobileMenuOpen} data-id="element-4201" data-mp="2o8lit8quw1gp">
                {mobileMenuOpen ? <X size={24} data-id="element-4202" data-mp="2fa02lk8uwyj3" /> : <Menu size={24} data-id="element-4203" data-mp="2f6cberqvwo7j" />}
              </button>
            </div>
          </div>
        </div>

        {/* THE MOBILE SHEET, rebuilt to the same language as the desktop menu.
         What made it feel generic: a white panel with cool neutral hairlines,
         nav set at the same 16px weight as body copy, an orange icon on every
         feature in a cramped two-column grid, three grey rounded-lg buttons
         crowded at the TOP — the first thing a reader met on opening the menu
         was a row of controls rather than the site.
         Now: an ink sheet (the same surface as the desktop dropdown, so both
         menus are one idea), destinations set in the display face at a size
         worth tapping, quiet icons, and the actions moved to the BOTTOM —
         where a thumb actually rests, and where they read as the conclusion of
         the menu rather than its greeting. */}
        <AnimatePresence data-id="element-4204" data-mp="26t9bymyg0sb1">
          {mobileMenuOpen && <motion.div key="mobile-sheet" initial={{
          opacity: 0,
          y: -8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.2,
          ease: [0.16, 1, 0.3, 1]
        }} className="md:hidden absolute top-full left-0 z-50 flex max-h-[84svh] w-full flex-col overflow-hidden rounded-b-[22px] border-b border-white/[0.08] bg-[#1A120C] shadow-[0_24px_60px_-20px_rgba(20,13,8,0.7)]" data-id="element-4205" data-mp="22orrsmo68czq">
              <div className="dot-matrix pointer-events-none opacity-[0.07]" aria-hidden="true" data-id="element-4206" data-mp="3rnxeddygwqca" />

              <div className="relative flex-1 overflow-y-auto px-5 pt-2" data-id="element-4207" data-mp="h2yi9z3thb5">
                {NAV_LABELS.map(label => label === 'Features' ? <div key={label} className="border-b border-white/[0.07]" data-id="element-4208" data-mp="59w0wr73odk2">
                    <button type="button" onClick={() => toggleSection('features')} className="font-title flex w-full items-center justify-between py-3.5 text-left text-[19px] font-bold tracking-tight text-[#FAF8F4]" aria-expanded={openSection === 'features'} data-id="element-4209" data-mp="a6w1iml6ko3s">
                      Products
                      <ChevronDown size={18} className={`text-white/40 transition-transform duration-200 ease-out ${openSection === 'features' ? 'rotate-180' : ''}`} data-id="element-4210" data-mp="3in7lai0lde29" />
                    </button>
                    {/* One column, not two. Seventeen labels in a 2-up grid
                     wrap onto second lines and shrink the tap target; a single
                     column reads down one edge and every row is thumb-sized. */}
                    {openSection === 'features' && <div className="space-y-4 pb-3" data-id="element-4211" data-mp="2ig2d9eiyhdds">
                      {featureGroups.map(group => <div key={group.key} data-id="element-4212" data-mp="2dp3zallfwrdp">
                        <p className="pb-1 text-[11px] font-semibold tracking-[0.02em] text-[#9A8B7C]" data-id="element-4213" data-mp="2do2dbm2vykh7">{group.label}</p>
                        <div data-id="element-4214" data-mp="28phik26ekt05">
                          {group.items.map(feature => feature && <Link key={feature.slug} to={`/features/${feature.slug}`} className="group/item -mx-2 flex items-center gap-2.5 rounded-[10px] px-2 py-2.5 text-[15px] font-semibold text-[#C9BFB4] transition-colors duration-150 ease-out active:bg-white/[0.06]" data-id="element-4215" data-mp="23rz9mbsdyds5">
                            <FeatureIcon name={feature.icon} size={16} className="shrink-0 text-white/35" data-id="element-4216" data-mp="1preova2wu7w0" />
                            <span data-id="element-4217" data-mp="3p4smne2lvsf1">{feature.menuLabel || feature.name}</span>
                          </Link>)}
                        </div>
                      </div>)}
                    </div>}
                  </div> : EXTERNAL_NAV.has(label) ? <a key={label} href={NAV_ROUTES[label]} target="_blank" rel="noopener noreferrer" className="font-title block border-b border-white/[0.07] py-3.5 text-[19px] font-bold tracking-tight text-[#FAF8F4]" data-id="element-4218" data-mp="fsjjk4n4myk0">
                    {label}
                  </a> : <Link key={label} to={NAV_ROUTES[label]} className="font-title flex items-center gap-2 border-b border-white/[0.07] py-3.5 text-[19px] font-bold tracking-tight text-[#FAF8F4]" data-id="element-4219" data-mp="14yishdq5bf9">
                    {label}{label === 'ChatGPT' && <span className="shrink-0 rounded-full bg-[#EA580C]/25 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#FDBA74]" data-id="element-4220" data-mp="3o9eodpnhit0q">New</span>}
                  </Link>)}
              </div>

              {/* Pinned to the foot of the sheet: the same three actions in the
               same ascending order as the desktop header, as pills rather than
               boxes so they match every other control on the site. */}
              <div className="relative border-t border-white/[0.08] bg-[#150F0A] px-5 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4" data-id="element-4221" data-mp="1ckn8hgid4ojr">
                <div className="mb-2.5 flex gap-2.5" data-id="element-4222" data-mp="1p2psmueru05a">
                  {/* Login is waitlisted for now — opens the waitlist instead
                   of the (preserved) /login page. */}
                  <WaitlistCTA analyticsLabel="mobile-menu-log-in" spinnerClassName="text-white" className="flex-1 rounded-full border border-white/15 px-4 py-2.5 text-center text-sm font-bold text-[#FAF8F4]" data-id="element-4223" data-mp="1tvsfkjp1mbci">
                    Log in
                  </WaitlistCTA>
                  <a href={TALK_TO_SALES_URL} target="_blank" rel="noopener noreferrer" className="flex-1 rounded-full border border-white/15 px-4 py-2.5 text-center text-sm font-bold text-[#FAF8F4]" data-id="element-4224" data-mp="164dgr0wh6c60">
                    Talk to sales
                  </a>
                </div>
                <WaitlistCTA analyticsLabel="mobile-menu-try-giga" className="w-full rounded-full bg-[#EA580C] px-4 py-3 text-center text-sm font-bold text-white" data-id="element-4225" data-mp="2mh235kc2pfoy">
                  Get started
                </WaitlistCTA>
                <p className="pt-2.5 text-center text-[12px] text-white/40" data-id="element-4226" data-mp="21r7vis0o5zqr">
                  $100 in credits free · No card required
                </p>
              </div>
            </motion.div>}
        </AnimatePresence>
      </header>

      <main className="flex-1" data-id="element-4227" data-mp="26s5lhqrx7y2t">
        <Outlet data-id="element-4228" data-mp="26t6zo448h0or" />
      </main>

      {/* Global floating "Try Giga for free" CTA — mobile only. Gives the user
                                    a persistent conversion tap as they scroll, even while the header is slid
                                    away on scroll-down. Hidden at the very top of the hero (where the hero
                                    owns its own primary CTA) and while an in-page section suppresses it.
                                    Over the orange footer it switches to a white treatment so it stays
                                    visible. */}
      <AnimatePresence data-id="element-4229" data-mp="vds516zhykwt">
        {isScrolled && !atHeroTop && !floatingCtaSuppressed ? <motion.div initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 24
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} className="md:hidden fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 pointer-events-none" data-id="element-4230" data-mp="370lw4qr9n0gt">
            <WaitlistCTA analyticsLabel="floating-cta" spinnerClassName={overOrange ? 'text-[#EA580C]' : 'text-white'} className={['pointer-events-auto inline-flex items-center justify-center px-6 py-2.5 rounded-full text-sm font-bold shadow-lg transition-colors', overOrange ? 'bg-white text-[#EA580C] shadow-black/20' : 'bg-[#EA580C] text-white shadow-[#EA580C]/30'].join(' ')} data-id="element-4231" data-mp="3pr38hkrsjjg0">
              Get started
            </WaitlistCTA>
          </motion.div> : null}
      </AnimatePresence>

      <footer ref={footerRef} data-cta-invert="true" className="relative overflow-hidden bg-[#2B211A] text-sm text-white/80 pt-20 pb-40 md:pb-56" data-id="element-4232" data-mp="25dcosq1u6dxq">
        {/* PHOTOGRAPHIC GROUND. A dusk ridgeline with one band of bright light
         at the horizon. Anchored to the BOTTOM (not centred) so the ridge sits
         under the sitemap and the open sky carries the links — default centring
         puts the link text straight over the brightest part of the frame.
         No overall scrim: the plate is already near-black, so a wash would only
         flatten it. The short gradient at the top edge exists purely to keep
         the seam with the cream section above from reading as a hard cut. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0" data-id="element-4233" data-mp="2ykefxx0vzwj3">
          <img src="https://cdn.magicpatterns.com/uploads/oezJmNYbrxjRHbMnorXSyL/hf_20260903_082104_b928195c-1db7-4b4e-a352-02eb71cf62c1_4.png" alt="" className="size-full object-cover object-bottom" data-id="element-4234" data-mp="2ymhej5g3uxzf" />
          <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#2B211A] to-transparent" data-id="element-4235" data-mp="1n34y1exepcjw" />
        </div>

        {/* Subtle grid texture, over the plate. */}
        <div className="dot-matrix opacity-[0.12]" aria-hidden="true" data-id="element-4236" data-mp="3uskinesvpyfl" />

        {/* Oversized ghosted brand wordmark — sits in the lower band of the
         footer (below the sitemap), gently bleeding off the bottom edge. */}
        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga-wordmark.svg" alt="" aria-hidden="true" className="pointer-events-none select-none absolute inset-x-0 -bottom-[2vw] mx-auto w-[80%] max-w-none object-contain opacity-[0.12] [filter:brightness(0)_invert(1)]" data-id="element-4237" data-mp="7kgshwvnj4rj" />

        <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-4238" data-mp="qe2tpb0zfi9d">
          {/* Sitemap. The three feature groups used to stack inside a single
           "Features" column, which ran ~17 links deep and left the other
           three columns empty for most of the footer's height. Each group is
           now its own column, so the row reads as five balanced lists. */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-10 md:gap-8 pb-16 border-b border-white/20" data-id="element-4239" data-mp="49sbfugdphu4">
            {/* Brand column */}
            <div className="col-span-2 md:col-span-3 lg:col-span-1" data-id="element-4240" data-mp="3vheizy67y4g9">
              <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className="h-7 object-contain mb-4 [filter:brightness(0)_invert(1)]" data-id="element-4241" data-mp="db0682jos0jg" />

              {/* The footer line is the one place the company describes ITSELF
               rather than sells a feature — it is read by people who have
               scrolled a whole page and by search engines quoting the site. A
               personality line ("everyone's new favorite colleague") is a
               tagline for one pillar, not a description of the platform. */}
              <p className="max-w-xs leading-relaxed text-white/70" data-id="element-4242" data-mp="10z19ox20cqs0">
                The AI platform for teams that want to go faster and stay in
                control.
              </p>

              {/* The two ways to reach a human, in one permanent place. Both
               were previously scattered — the calendar behind buttons on some
               pages only, and the address buried in the legal pages — so a
               reader on any other page had no way to get in touch at all. */}
              <div className="mt-6 flex flex-col items-start gap-2 text-[14px]" data-id="element-4243" data-mp="11uli4l6yjjzo">
                <a href={TALK_TO_SALES_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 font-semibold text-white transition-colors hover:text-[#FDBA74]" data-id="element-4244" data-mp="11sh2x73lyyk3">
                  Book a call with sales
                  <span aria-hidden="true" data-id="element-4245" data-mp="wufmktxkhdcb">→</span>
                </a>
                <a href={SUPPORT_MAILTO} className="text-white/70 transition-colors hover:text-white" data-id="element-4246" data-mp="ivfwriybisek">
                  {SUPPORT_EMAIL}
                </a>
              </div>
            </div>

            {/* One column per feature group, so no single list runs long. */}
            {featureGroups.map(group => <div key={group.key} data-id="element-4247" data-mp="ydh85f8aosbi">
                <h3 className="mb-4 text-[11px] font-bold uppercase tracking-[1.5px] text-white/60" data-id="element-4248" data-mp="yeikro4ylmvc">
                  {group.label}
                </h3>
                <ul className="space-y-2.5" data-id="element-4249" data-mp="tg7cviy85mfr">
                  {group.items.map(feature => feature && <li key={feature.slug} data-id="element-4250" data-mp="ohdxtm3ahndh">
                      <Link to={`/features/${feature.slug}`} className="group/footer inline-flex items-center gap-2 text-white/80 transition-colors hover:text-white" data-id="element-4251" data-mp="ke7q0a0j3o4w">
                        <FeatureIcon name={feature.icon} size={14} className="shrink-0 text-white/55 transition-colors group-hover/footer:text-white" data-id="element-4252" data-mp="fl50ccyvgytz" />
                        <span data-id="element-4253" data-mp="5q4lz661qmiq">{feature.menuLabel || feature.name}</span>
                      </Link>
                    </li>)}
                </ul>
              </div>)}

            {/* Product and Legal share the last column — together they are
             about the same depth as one feature group. */}
            <div className="space-y-8" data-id="element-4254" data-mp="28ap2y16glapv">
              {[{
              title: 'Product',
              links: [{
                label: 'Pricing',
                to: '/pricing'
              }, {
                label: 'Developers',
                to: '/developers'
              }]
            }, {
              title: 'Legal',
              links: [{
                label: 'Support',
                to: '/support'
              }, {
                label: 'Privacy',
                to: '/privacy'
              }, {
                label: 'Terms',
                to: '/terms'
              }, {
                label: 'Vulnerability Disclosure',
                to: '/vulnerability-disclosure'
              }]
            }].map(col => <div key={col.title} data-id="element-4255" data-mp="23eguf6mrofro">
                  <h3 className="mb-4 text-[11px] font-bold uppercase tracking-[1.5px] text-white/60" data-id="element-4256" data-mp="15xi4xw08vifr">
                    {col.title}
                  </h3>
                  <ul className="space-y-2.5" data-id="element-4257" data-mp="1au048gct2okp">
                    {col.links.map(link => <li key={link.label} data-id="element-4258" data-mp="kngxyv5qtoa7">
                        <Link to={link.to} className="text-white/80 transition-colors hover:text-white" data-id="element-4259" data-mp="pl8q9qaavcex">
                          {link.label}
                        </Link>
                      </li>)}
                  </ul>
                </div>)}
            </div>
          </div>

          {/* Bottom bar */}
          <div className="pt-8 flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-4260" data-mp="1vxyuzeuaudg">
            <div className="text-white/60" data-id="element-4261" data-mp="6mecokmkoi0c">
              © {new Date().getFullYear()} Tonic Labs Limited. All rights reserved.
            </div>
            <div className="flex gap-6" data-id="element-4262" data-mp="3o479tqo0avjt">
              {[{
              label: 'Twitter',
              href: 'https://x.com/askgiga'
            }].map(social => <a key={social.label} href={social.href} target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white transition-colors" data-id="element-4263" data-mp="3f27qustflhe8">
                  {social.label}
                </a>)}
            </div>
          </div>
        </div>
      </footer>
    </div>;
}